import { useState, useEffect } from "react";
import PriorityClaimBox from "./PriorityClaimBox";
import SeniorityClaimBox from "./SeniorityClaimBox";
import InfoBox from "../../ui/InfoBox";
import SectionHeader from "../../ui/SectionHeader";
import { FiAward } from "react-icons/fi";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { getPriorities } from "../../../services/priorityService";
import { getSeniorities } from "../../../services/seniorityService";

interface ClaimsSectionProps {
  onPriorityChange?: (claims: any[]) => void;
  onSeniorityChange?: (claims: any[]) => void;
  initialPriorities?: any[];
  initialSeniorities?: any[];
  disabled?: boolean;
}

export default function ClaimsSection({
  onPriorityChange,
  onSeniorityChange,
  initialPriorities,
  initialSeniorities,
  disabled = false,
}: ClaimsSectionProps) {
  const [enabled, setEnabled] = useState(false);
  const [distinctive, setDistinctive] = useState(false);
  const [claimType, setClaimType] = useState("main");

  const [loadedPriorities, setLoadedPriorities] = useState<any[]>([]);
  const [loadedSeniorities, setLoadedSeniorities] = useState<any[]>([]);

  const token = useSelector((state: RootState) => state.auth.token);

  useEffect(() => {
    if (
      (initialPriorities && initialPriorities.length > 0) ||
      (initialSeniorities && initialSeniorities.length > 0)
    ) {
      setEnabled(true);
    }
  }, [initialPriorities, initialSeniorities]);

  useEffect(() => {
    async function load() {
      if (!token) return;

      const allPrios = await getPriorities();
      const allSens = await getSeniorities();

      setLoadedPriorities(
        (initialPriorities ?? []).map((p: any) =>
          typeof p === "string"
            ? (allPrios.find((x: any) => x._id === p) ?? { _id: p })
            : p,
        ),
      );

      setLoadedSeniorities(
        (initialSeniorities ?? []).map((s: any) =>
          typeof s === "string"
            ? (allSens.find((x: any) => x._id === s) ?? { _id: s })
            : s,
        ),
      );
    }

    load();
  }, [initialPriorities, initialSeniorities, token]);

  return (
    <section className="scroll-mt-28 border-t border-gray-300 pt-4">
      <SectionHeader
        icon={<FiAward size={20} />}
        title="Claims"
        subtitle="Priority, seniority or acquired distinctiveness (optional)."
      />

      <label
        className={`flex items-center gap-2 ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <div className="relative">
          <input
            type="checkbox"
            checked={enabled}
            disabled={disabled}
            onChange={(e) => {
              if (!disabled) {
                setEnabled(e.target.checked);
              }
            }}
            className="sr-only"
          />
          <div
            className={`w-10 h-5 rounded-full ${enabled ? "bg-green-600" : "bg-gray-300"}`}
          />
          <div
            className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
              enabled ? "translate-x-5" : ""
            }`}
          />
        </div>
        <span className="font-semibold text-sm text-gray-700">
          I want to claim priority, seniority, acquired distinctiveness or other
          claims (optional)
        </span>
      </label>

      {enabled && (
        <div className="border border-gray-200 rounded-md p-6 space-y-6 mt-6 bg-white shadow-sm">
          <div className="flex items-center gap-2">
            <img src="/section.svg" alt="Section" className="w-4 h-4" />
            <h4 className="font-semibold text-black">
              Select the claims you wish to add
            </h4>
          </div>
          <p className="text-sm text-gray-700 -mt-2">
            You can choose between different types of claims.
          </p>

          <div className="space-y-6">
            <PriorityClaimBox
              onChange={onPriorityChange}
              claims={loadedPriorities}
              initialData={loadedPriorities}
            />
            <SeniorityClaimBox
              onChange={onSeniorityChange}
              claims={loadedSeniorities}
              initialData={loadedSeniorities}
            />
          </div>

          <div className="pt-4 border-t border-gray-200">
            <label
              className={`flex items-center gap-2 text-sm text-gray-700 ${disabled ? "opacity-50" : ""}`}
            >
              <input
                type="checkbox"
                checked={distinctive}
                disabled={disabled}
                onChange={(e) => {
                  if (!disabled) {
                    setDistinctive(e.target.checked);
                  }
                }}
                className="h-4 w-4 border-gray-300 rounded"
              />
              <span>The mark has acquired distinctiveness through use.</span>
              <InfoBox title="Acquired distinctiveness">
                Acquired distinctiveness means that the trademark has become
                distinctive through use in the market, even if it was not
                inherently distinctive at first. The applicant must provide
                sufficient evidence demonstrating that consumers associate the
                mark with the goods or services of the applicant.
                <br />
                <br />
                The claim can be made either as a main claim or as an
                alternative claim. The Office will examine and decide on the
                distinctiveness accordingly.
              </InfoBox>
            </label>
          </div>

          {distinctive && (
            <div className="border-t border-gray-200 pt-5 space-y-3">
              <span className="block text-sm font-semibold text-black">
                Type of claim
              </span>

              <div
                className={`flex flex-col sm:flex-row gap-6 text-sm text-gray-700 ${disabled ? "opacity-50" : ""}`}
              >
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="claimType"
                    value="main"
                    checked={claimType === "main"}
                    disabled={disabled}
                    onChange={(e) => {
                      if (!disabled) {
                        setClaimType(e.target.value);
                      }
                    }}
                  />
                  Main claim
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="claimType"
                    value="alternative"
                    checked={claimType === "alternative"}
                    disabled={disabled}
                    onChange={(e) => {
                      if (!disabled) {
                        setClaimType(e.target.value);
                      }
                    }}
                  />
                  Alternative claim
                </label>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
